/**
 * EVENTSFLO-PORTAL-I18N-PHASE-2A-FRAMEWORK-MIGRATION-V1
 *
 * Thin React wrapper around react-i18next that preserves the existing
 * useI18n() API surface so Phase 1 consumers (ClientPortalPage,
 * CrewPortalPage, LanguageSelector) keep working unchanged.
 *
 * Backward-compatible exports:
 *   - <I18nProvider> mounts the i18next instance into the React tree.
 *   - useI18n() returns { language, setLanguage, t, availableLanguages }
 *     just like Phase 1.
 *   - LANGUAGE_OPTIONS re-exported.
 *
 * Forward-compatible:
 *   - Components can switch to `useTranslation('crewPortal')` from
 *     react-i18next directly for namespace-scoped translation.
 */
import { useCallback, useEffect, useMemo } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n, {
  LANGUAGE_OPTIONS,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from "./index";

export { LANGUAGE_OPTIONS };

export function I18nProvider({ children }) {
  // Mirror language changes back into <html lang="…"> for screen readers
  // and persist into localStorage so existing user choices survive.
  useEffect(() => {
    const onChange = (lng) => {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, lng);
        }
        if (typeof document !== "undefined" && document.documentElement) {
          document.documentElement.setAttribute("lang", lng);
        }
      } catch {
        /* ignore */
      }
    };
    // Fire once on mount for the initial language.
    onChange(i18n.language);
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

/**
 * Backward-compatible hook matching the Phase 1 shape exactly.
 *
 * The Phase 1 API expected dotted keys with the namespace as the first
 * segment, e.g. `t("crewPortal.nav.shifts")`. We translate that into
 * i18next's `t('crewPortal:nav.shifts')` syntax automatically so all
 * existing call sites continue to work without any refactor.
 */
export function useI18n() {
  // `t` here is the default-namespace translator from react-i18next.
  const { t: i18nT, i18n: instance } = useTranslation();

  const t = useCallback(
    (key, fallbackOrOptions) => {
      if (typeof key !== "string") {
        if (
          fallbackOrOptions &&
          typeof fallbackOrOptions === "object" &&
          "defaultValue" in fallbackOrOptions
        ) {
          return fallbackOrOptions.defaultValue;
        }
        return fallbackOrOptions ?? key;
      }
      // Phase 2B: second arg may be a Phase 1 string fallback OR an
      // i18next options object (e.g. `{ count, name, defaultValue }`).
      // Normalise into i18next options.
      let options;
      let fallback;
      if (
        fallbackOrOptions &&
        typeof fallbackOrOptions === "object" &&
        !Array.isArray(fallbackOrOptions)
      ) {
        options = { ...fallbackOrOptions };
        fallback = options.defaultValue;
      } else {
        fallback = fallbackOrOptions;
        options = { defaultValue: fallback };
      }
      const colonIdx = key.indexOf(":");
      const dotIdx = key.indexOf(".");
      let translated;
      if (colonIdx !== -1) {
        // Already namespaced via `ns:key`.
        translated = i18nT(key, options);
      } else if (dotIdx !== -1) {
        // Phase 1 convention — first segment is the namespace.
        const ns = key.slice(0, dotIdx);
        const rest = key.slice(dotIdx + 1);
        translated = i18nT(`${ns}:${rest}`, options);
      } else {
        // Lone key — assume the default `common` namespace.
        translated = i18nT(key, options);
      }
      // i18next can return the key when no translation found; route
      // through the explicit fallback to honour Phase 1 semantics.
      if (translated === key || translated === undefined) {
        return fallback ?? key;
      }
      return translated;
    },
    [i18nT],
  );

  const language = instance.language || "en";

  const setLanguage = useCallback((code) => {
    if (SUPPORTED_LANGUAGES.includes(code)) {
      instance.changeLanguage(code);
    }
  }, [instance]);

  return useMemo(
    () => ({
      language,
      setLanguage,
      t,
      availableLanguages: LANGUAGE_OPTIONS,
    }),
    [language, setLanguage, t],
  );
}

export default I18nProvider;
